import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import keycode from 'keycode';
import { Backdrop, Button, Icon } from '@econ/ui-elements';
import Text, { text as textById } from '../../../shared/components/l10n/Text';
import { EContext } from '../../../shared/components/EContext';
import GlobalKeyboardShortcuts from '../../../shared/components/GlobalKeyboardShortcuts';
import useCachedQuery from '../../../shared/hooks/useCachedQuery';
import Results from './Results';
import generatePageShortcuts from './util/generatePageShortcuts';
import generateRouteShortcuts from './util/generateRouteShortcuts';
import { Book, Journal } from '../../../../types';
import './style.css';

interface Props {
    data?: any;
    history: any;
    routes?: any;
    texts?: any;
}

interface State {
    cache: any[];
    currentView: any[];
    isOpen: boolean;
    results: any[];
    value: string;
}

class Quicksearch extends Component<Props, State> {
    state = {
        cache: generateRouteShortcuts(this.props.data, this.props.routes, this.props.texts),
        currentView: [],
        isOpen: false,
        results: [],
        value: '',
    };

    // refs
    input: React.RefObject<any> = React.createRef();

    // timers
    pageShortcutsInterval = undefined;

    componentDidUpdate(prevProps, prevState) {
        if (this.props.routes !== prevProps.routes || JSON.stringify(this.props.data) !== JSON.stringify(prevProps.data)) {
            this.setState({ cache: generateRouteShortcuts(this.props.data, this.props.routes, this.props.texts) });
        }

        if (!prevState.isOpen && this.state.isOpen) {
            this.input.current.focus();
        }
    }

    render() {
        const { cache, isOpen, results, value } = this.state;
        const className = isOpen ? 'quicksearch--open' : undefined;
        const icon = isOpen ? 'close' : 'search';

        return (
            <GlobalKeyboardShortcuts handlers={{ '.': this.open }}>
                <div id="quicksearch" className={className}>
                    {isOpen && <Backdrop onClick={this.close} />}
                    <label>
                        <input
                            onChange={this.onChange}
                            onFocus={this.open}
                            onKeyDown={this.onKeyDown}
                            placeholder={textById(this.props.texts)('Go to...', 'QUICK_SEARCH_PLACEHOLDER')}
                            ref={this.input}
                            type="search"
                            value={value}
                        />
                        <Button onClick={this.onClick} tabIndex={-1} type="discrete">
                            <Icon name={icon} />
                        </Button>
                    </label>
                    {isOpen && (
                        <div className="quicksearch__results">
                            {results.length > 0 ? (
                                <Results cache={cache} close={this.close} results={results} history={this.props.history} />
                            ) : (
                                <p>
                                    <Text id="QUICK_SEARCH_NO_RESULTS">No results. Try using a different keyword.</Text>
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </GlobalKeyboardShortcuts>
        );
    }

    onClick = () => {
        if (this.state.isOpen) {
            this.close();
        } else {
            this.open();
        }
    };

    onChange = e => {
        const value = e.target.value;

        this.setState(state => ({
            isOpen: true,
            results: this.search(value, state.cache, state.currentView),
            value,
        }));
    };

    onKeyDown = e => {
        if (e.keyCode === keycode('tab')) {
            this.close();
            return;
        }

        if (e.keyCode === keycode('escape')) {
            e.preventDefault();
            this.close();
            this.input.current.blur();
            return;
        }
    };

    open = (event?: any) => {
        if (event && event.preventDefault) {
            event.preventDefault();
        }

        const setPageShortcuts = shortcuts =>
            this.setState(state => ({
                currentView: shortcuts,
                isOpen: true,
                results: this.search(state.value, state.cache, shortcuts),
            }));

        this.setState(
            {
                isOpen: true,
            },
            () => {
                generatePageShortcuts().then(shortcuts => {
                    if (shortcuts.length) {
                        setPageShortcuts(shortcuts);
                    } else {
                        // check for shortcuts again after 2.5s, incase they opened quicksearch before the page loaded
                        setTimeout(() => {
                            generatePageShortcuts().then(pageShortcuts => {
                                if (pageShortcuts.length) {
                                    setPageShortcuts(pageShortcuts);
                                }
                            });
                        }, 2500);
                    }
                });
            }
        );
    };

    close = () =>
        this.setState({
            isOpen: false,
            value: '',
        });

    search = (value, cache, currentView) => {
        const data = [...currentView, ...cache];

        return value.length > 0
            ? data.filter(c => c.title.toLowerCase().includes(value.toLowerCase()) && c.type !== 'tab')
            : currentView;
    };
}

export default withRouter(props => {
    const { routes, texts } = React.useContext(EContext);
    const hasJournalAccess = routes['/accounting/journals/:id'];
    const hasInventoryBookAccess = routes['/inventory/books/:id'];
    const hasProjectBookAccess = routes['/projects/usage/:id'];

    if (!hasJournalAccess && !hasInventoryBookAccess && !hasProjectBookAccess) {
        return <Quicksearch {...props} routes={routes} texts={texts} />;
    }

    // if (hasJournalAccess)
    const journalsQuery = useCachedQuery<Journal[]>('/journals?type=classic', []);

    // if (hasInventoryBookAccess || hasProjectBookAccess)
    const booksQuery = useCachedQuery<Book[]>('/inventory/books', []);

    return (
        <Quicksearch
            {...props}
            data={{
                journals: journalsQuery.data,
                inventoryBooks: booksQuery.data,
            }}
            routes={routes}
            texts={texts}
        />
    );
});
